import type { ICellRendererParams } from "ag-grid-community";

import { isEmptyValue } from "../../cellValues";
import { EmptyCell } from "../EmptyCell";

import styles from "./TimestampCell.module.css";
import { useRelativeTick } from "./useRelativeTick";

export type TimestampVariant = "precise" | "relative";

/** Below this age, `relative` renders live "N minutes ago" text and ticks. */
const RELATIVE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/**
 * Formatters resolved once against the viewer's locale (`undefined` picks up the
 * runtime default, honoring the browser's full language-preference list). All
 * render in the viewer's local time zone with no inline zone label — since
 * everything is local the zone is constant; the tooltip carries the UTC instant.
 */
const dateFmt = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

// `precise`: hours, minutes, seconds. No millis intentionally.
const preciseTimeFmt = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
});

// `relative` (>= 24h): trimmed — no seconds
const trimmedTimeFmt = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});

const relativeFmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

type TimestampCellProps = ICellRendererParams & { variant?: TimestampVariant };

/**
 * The authoritative UTC instant for the tooltip, e.g.
 * "2025-03-18 16:54:37.123 UTC". Derived from the already-computed `iso` (no
 * second `toISOString`); the visible cell already shows the local time.
 */
function utcTooltip(iso: string): string {
  return iso.replace("T", " ").replace("Z", " UTC");
}

/**
 * Locale-aware relative phrasing. `Intl.RelativeTimeFormat` localizes a
 * (value, unit) pair — it does not pick the unit, so this only buckets into
 * "now" / minutes / hours. Floored to minutes, matching the 60s tick.
 */
function formatRelative(instant: Date): string {
  const mins = Math.round((instant.getTime() - Date.now()) / 60_000); // < 0 = past
  if (mins > -1) {
    return relativeFmt.format(0, "second"); // < ~60s ago → localized "now"
  }
  if (mins > -60) {
    return relativeFmt.format(mins, "minute");
  }
  return relativeFmt.format(Math.round(mins / 60), "hour");
}

/** Stacked date over time, wrapped in a `<time>` carrying the exact instant. */
function StackedTimestamp({
  instant,
  iso,
  title,
  time,
}: {
  instant: Date;
  iso: string;
  title: string;
  time: string;
}) {
  return (
    <time dateTime={iso} title={title} className={styles.stack}>
      <span className={styles.date}>{dateFmt.format(instant)}</span>
      <span className={styles.time}>{time}</span>
    </time>
  );
}

/**
 * Adaptive `relative` renderer: under 24h it shows live relative text and
 * subscribes to the shared ticker; at/after 24h it shows the trimmed stacked
 * form and never subscribes. On the tick that crosses 24h, `recent` flips, the
 * cell re-renders into the static form, and the effect cleanup unsubscribes it.
 */
function RelativeTimestamp({
  instant,
  iso,
  title,
}: {
  instant: Date;
  iso: string;
  title: string;
}) {
  const recent = Date.now() - instant.getTime() < RELATIVE_THRESHOLD_MS;
  useRelativeTick(recent);

  if (!recent) {
    return (
      <StackedTimestamp
        instant={instant}
        iso={iso}
        title={title}
        time={trimmedTimeFmt.format(instant)}
      />
    );
  }
  return (
    <time dateTime={iso} title={title} className={styles.inline}>
      {formatRelative(instant)}
    </time>
  );
}

/**
 * AG Grid `cellRenderer` for timestamp columns, rendered in the viewer's local
 * time and locale.
 *
 * Variants:
 *  - `precise` (default): stacked date over full time (with seconds), for
 *    detail-critical timestamps such as event boundaries.
 *  - `relative`: under 24h, live "N minutes ago"; at/after 24h, stacked date
 *    over trimmed time (no seconds). For created/modified timestamps.
 */
export function TimestampCell({
  value,
  variant = "precise",
}: TimestampCellProps) {
  if (isEmptyValue(value)) {
    return <EmptyCell />;
  }
  const instant = new Date(value as string | number | Date);
  if (Number.isNaN(instant.getTime())) {
    return <EmptyCell />;
  }
  const iso = instant.toISOString();
  const title = utcTooltip(iso);

  if (variant === "relative") {
    return <RelativeTimestamp instant={instant} iso={iso} title={title} />;
  }
  return (
    <StackedTimestamp
      instant={instant}
      iso={iso}
      title={title}
      time={preciseTimeFmt.format(instant)}
    />
  );
}
